import { z } from "zod";
import { CourseTestSlide } from "./course-test.ts";
import { LESSON_EXERCISE_SUBJECTS } from "./lesson-exercise.ts";

/**
 * Пачка заданий на уроке: контракт сборки
 * `POST /v1/lessons/:lessonId/exercise-batch`.
 * Проект целиком в `docs/room-exercise-batch-design.md`.
 *
 * Преподаватель в комнате выбирает несколько строк «тема, уровень, сколько
 * заданий», как в форме домашки, и одним нажатием получает всю пачку,
 * разложенную по слайдам. Сервер собирает задания и кладёт эталоны, на доску их
 * ставит клиент, ровно как у теста на весь курс.
 *
 * ⚠️ **Ответов здесь нет и быть не может**: файл реэкспортирует баррель
 * `@edu/shared`, а его читает браузер ученика (раздел 5.3 CLAUDE.md). Реестры
 * генераторов живут за барьером, и сборка идёт на сервере
 * (`apps/api/src/lesson-topic-batch.ts`).
 *
 * ⚠️ **Одна ручка, а не N вызовов ручки одного задания**, по тем же трём
 * причинам, что у теста на курс: лимит частоты, атомарность и замок комнаты
 * (раздел 3 проекта).
 */

/**
 * Потолок пачки: сколько заданий собирается за одно нажатие.
 *
 * Двенадцать, и довод по порядку веса (4.1 проекта): тест на курс кладёт тем
 * же путём до тринадцати, то есть объём уже обкатан на стенде; двенадцать
 * заданий это от четырёх до шести слайдов, то есть то, что пролистывают за
 * урок, а полсотни, как у домашки, это уже лента, а не колода; пример
 * преподавателя это восемь заданий, и потолок в полтора раза выше покрывает
 * «ещё пару по третьей теме».
 *
 * ⚠️ **Одна константа на обе стороны.** Форма не отпускает кнопку сверх неё
 * (`maxCountFor` и `rowProblem` с этим потолком), сервер передаёт её в
 * `takeVariants` как `hardCap`. Разойдись два числа, форма отпускала бы то, в
 * чём сервер отказывает, или наоборот.
 *
 * Потолок это граница одного нажатия, а не урока: вторую пачку можно собрать
 * сразу следом.
 *
 * ⚠️ Не `LESSON_BATCH_MAX`: это имя уже занято потолком пачки УРОКОВ
 * (`./room.ts`, тридцать). С одинаковым именем баррель не собирается, а
 * перепутанное число молча дало бы форме урока потолок тридцать.
 */
export const LESSON_EXERCISE_BATCH_MAX = 12;

/**
 * Строка заказа: тема, уровень, количество.
 *
 * По форме это подмножество `OgeTopicPick` домашки (там уровень необязателен, а
 * количество до 50), поэтому серверные помощники домашки (`mergePicks`,
 * `refusalToError`, `engRefusalToError`) принимают строки пачки без приведения.
 */
export const LessonBatchRow = z.object({
  /**
   * ⚠️ Свободная строка, а не юнион слагов, по той же причине, что у
   * `OgeTopicPick` и `LessonTopicExerciseSource`: неизвестный слаг обязан
   * отвечать `generator_unavailable` с перечнем того, что есть, а не безликим
   * `invalid_body` от схемы.
   */
  skill: z.string().min(1).max(100),
  /**
   * 1 лёгкий, 2 средний, 3 сложный.
   *
   * ⚠️ **ОБЯЗАТЕЛЕН, в отличие от заказа одного задания.** Там «любой» уровень
   * это умолчание и несущее свойство: повторные нажатия ходят по разным
   * генераторам темы. В пачке разнообразие даёт само количество, а обязательный
   * уровень держит обещание формы: предпросмотр состава совпадает с тем, что
   * соберёт сервер (`composition` в `oge/topic-form.ts`).
   */
  difficulty: z.number().int().min(1).max(3),
  count: z.number().int().min(1).max(LESSON_EXERCISE_BATCH_MAX),
});
export type LessonBatchRow = z.infer<typeof LessonBatchRow>;

/** Сколько заданий заказано всего: сумма `count` по строкам. Её зовут и схема, и форма. */
export function lessonBatchTotal(rows: readonly { readonly count: number }[]): number {
  return rows.reduce((sum, row) => sum + row.count, 0);
}

/**
 * Заказ пачки.
 *
 * ⚠️ **`batchId` это ключ идемпотентности, и он обязателен**, как `testId` у
 * теста на курс: идентификаторы заданий выводятся из него детерминированно,
 * поэтому повтор потерянного запроса попадает в уже записанные строки и
 * возвращает ту же пачку, без второго вызова генераторов.
 *
 * ⚠️ **Потолок суммы стоит в схеме, и отказ по нему это `invalid_body`**, нового
 * кода нет. Форма сверх потолка кнопку не отпускает, то есть дойти сюда с такой
 * суммой может только устаревшая вкладка или запрос руками, и для обоих верный
 * текст ровно тот, что уже стоит за `invalid_body`. У домашки смысловой потолок
 * отвечает своим кодом (`homework_full`), потому что сумму там складывают
 * источники, которых схема целиком не видит; здесь сумма это сумма одного поля
 * одного массива, и схема видит её целиком.
 */
export const LessonBatchRequest = z
  .object({
    batchId: z.string().min(8).max(64),
    subject: z.enum(LESSON_EXERCISE_SUBJECTS),
    rows: z.array(LessonBatchRow).min(1).max(LESSON_EXERCISE_BATCH_MAX),
  })
  .superRefine((body, ctx) => {
    const total = lessonBatchTotal(body.rows);
    if (total > LESSON_EXERCISE_BATCH_MAX) {
      ctx.addIssue({
        code: "custom",
        path: ["rows"],
        message: `заданий ${String(total)}, потолок ${String(LESSON_EXERCISE_BATCH_MAX)}`,
      });
    }
  });
export type LessonBatchRequest = z.infer<typeof LessonBatchRequest>;

/**
 * Собранная пачка.
 *
 * ⚠️ **Слайд это `CourseTestSlide`, взятый импортом, а не вторая схема**: смысл
 * один («слайд с разложенными заданиями, пустых не бывает»), и раскладка одна
 * (`apps/api/src/exercise-slides-layout.ts`).
 *
 * ⚠️ **Числа тем в ответе нет**, в отличие от теста: там темы выбирает сервер,
 * здесь их выбрал сам преподаватель секунду назад.
 *
 * `already` отличает повтор от новой сборки, как у теста и у ручки одного
 * задания.
 */
export const LessonBatchResponse = z.object({
  slides: z.array(CourseTestSlide),
  already: z.boolean().default(false),
});
export type LessonBatchResponse = z.infer<typeof LessonBatchResponse>;
